'use client';

import { useCallback } from 'react';
import { useScheduler } from '../SchedulerContext';
import { callServerAction } from '@/util/functions/fetching/callServerAction';

function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return undefined;
}

export function useWhatConvertsEvents() {
  const { wcProfileId, jwtToken } = useScheduler();

  const createLead = useCallback(
    async (leadData: {
      quoteId: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
    }): Promise<string | null> => {
      if (!wcProfileId) {
        console.log('[WC Lead] No WhatConverts profile ID, skipping lead');
        return null;
      }

      try {
        const wcClientCurrent = getCookie('wc_client_current');

        const response = await callServerAction<{ lead_id?: string }>({
          url: 'whatconverts/lead',
          method: 'post',
          headers: { 'client-token': jwtToken },
          data: {
            profile_id: wcProfileId,
            lead_type: 'web_form',
            attik_quote_id: leadData.quoteId,
            first_name: leadData.firstName,
            last_name: leadData.lastName,
            email: leadData.email,
            phone_number: leadData.phone,
            wc_client_current: wcClientCurrent,
          },
        });

        const leadId = response?.lead_id?.toString() || null;
        console.log('[WC Lead] Lead created:', leadData.quoteId, leadId);
        return leadId;
      } catch (error) {
        console.error('[WC Lead] Failed to create lead:', error);
        return null;
      }
    },
    [wcProfileId, jwtToken]
  );

  const updateLead = useCallback(
    async (
      leadId: string,
      data: {
        quoteValue?: number;
        salesValue?: number;
      }
    ): Promise<boolean> => {
      if (!wcProfileId) {
        console.log('[WC Lead] No WhatConverts profile ID, skipping lead update');
        return false;
      }

      try {
        await callServerAction({
          url: `whatconverts/lead/${leadId}`,
          method: 'post',
          headers: { 'client-token': jwtToken },
          data: {
            quote_value: data.quoteValue,
            sales_value: data.salesValue,
          },
        });

        console.log('[WC Lead] Lead updated:', leadId, data);
        return true;
      } catch (error) {
        console.error('[WC Lead] Failed to update lead:', error);
        return false;
      }
    },
    [wcProfileId, jwtToken]
  );

  return { createLead, updateLead };
}
